import { Dialog, Transition } from '@headlessui/react';
import { Fragment, ReactNode } from 'react';
import Loader from './Loader';

export interface ModalActionData {
  type: 'primary' | 'secondary';
  title: string;
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
}

export type ModalActionsAlignment = 'start' | 'end' | 'stretch';

interface Props {
  open: boolean;
  onClose: () => void;
  actions?: ModalActionData[];
  actionsAlignment?: ModalActionsAlignment;
  children?: ReactNode;
}

export default function FullScreenModal({
  open,
  onClose,
  children,
  actions,
  actionsAlignment,
}: Props) {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="z-999 fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={
                  'relative h-screen min-h-full w-full transform overflow-hidden bg-white text-left shadow-xl transition-all'
                }
              >
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  {/* <button
                    type="button"
                    className="bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={() => onClose()}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button> */}
                </div>
                {children}
                {actions?.length ? (
                  <ModalActions
                    actions={actions}
                    alignment={actionsAlignment}
                  />
                ) : (
                  <></>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

function ModalActions({
  actions,
  alignment,
}: {
  actions: ModalActionData[];
  alignment?: ModalActionsAlignment;
}) {
  return (
    <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
      {actions.map((action) => (
        <ModalAction
          key={JSON.stringify(action)}
          action={action}
          alignment={alignment}
        />
      ))}
    </div>
  );
}

function ModalAction({
  action,
}: {
  action: ModalActionData;
  alignment?: ModalActionsAlignment;
}) {
  const content = (
    <>
      {!action.loading ? (
        action.title
      ) : (
        <div className="relative">
          <div className="pointer-events-none opacity-0">{action.title}</div>
          <div className="absolute bottom-0 left-0 right-0 top-0">
            <Loader size={4} />
          </div>
        </div>
      )}
    </>
  );
  switch (action.type) {
    case 'primary':
      return (
        <button
          type="button"
          disabled={action.disabled}
          className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2"
          onClick={() => action.onClick?.()}
        >
          {content}
        </button>
      );

    case 'secondary':
      return (
        <button
          type="button"
          disabled={action.disabled}
          className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
          onClick={() => action.onClick?.()}
        >
          {content}
        </button>
      );
  }
}
